// apps/client/src/ui-kit/ChestUnlock.tsx
//
// Treasure Chest with 3 Padlocks — gamified ad bundle mechanic.
// Each ad watched removes one padlock with animation.
// After all 3 → chest opens → cards fly out with confetti.
//

import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { icChest3d, icChestOpen3d, icKey, icUnlocked } from '../assets';

interface ChestUnlockProps {
    /** 0-3: how many ads watched so far */
    progress: number;
    /** Whether an ad is currently being watched */
    isLoading: boolean;
    /** Called when user taps to watch the next ad */
    onWatchAd: () => void;
    /** How many cards are awarded when all 3 are done */
    rewardCards?: number;
    /** Whether the chest just opened (show celebration) */
    justCompleted?: boolean;
}

export function ChestUnlock({
    progress,
    isLoading,
    onWatchAd,
    rewardCards = 5,
    justCompleted = false,
}: ChestUnlockProps) {
    const isComplete = progress >= 3;
    const { t } = useTranslation();

    return (
        <div className="bg-white rounded-3xl p-5 shadow-card-white border border-white/30 text-center">
            {/* Chest icon */}
            <div className="relative inline-block mb-3">
                <motion.div
                    className="w-20 h-20 mx-auto"
                    animate={
                        isComplete
                            ? { scale: [1, 1.2, 1], rotate: [0, -5, 5, 0] }
                            : {}
                    }
                    transition={{ duration: 0.5 }}
                >
                    <img src={isComplete ? icChestOpen3d : icChest3d} alt="" className="w-full h-full" />
                </motion.div>

                {/* Padlock indicators */}
                {!isComplete && (
                    <div className="flex justify-center gap-2 mt-2">
                        {[0, 1, 2].map((i) => (
                            <AnimatePresence key={i} mode="wait">
                                {i < progress ? (
                                    <motion.span
                                        key="unlocked"
                                        initial={{ scale: 1.5, rotate: 20 }}
                                        animate={{ scale: 1, rotate: 0 }}
                                        exit={{ scale: 0, rotate: -45 }}
                                        className="w-6 h-6 inline-block"
                                    >
                                        <img src={icUnlocked} alt="" className="w-full h-full" />
                                    </motion.span>
                                ) : (
                                    <motion.span
                                        key="locked"
                                        className="w-6 h-6 inline-block opacity-40"
                                    >
                                        <img src={icKey} alt="" className="w-full h-full" />
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        ))}
                    </div>
                )}
            </div>

            {/* Title & description */}
            {isComplete || justCompleted ? (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h3 className="font-black text-[#1A0B2E] text-lg mb-1">
                        {t('chest.unlocked')}
                    </h3>
                    <p className="text-[#1A0B2E]/60 text-sm">
                        {t('chest.unlockedCards', { count: rewardCards })}
                    </p>
                </motion.div>
            ) : (
                <>
                    <h3 className="font-black text-[#1A0B2E] text-lg mb-1">
                        {t('chest.title')}
                    </h3>

                    <Button
                        onClick={onWatchAd}
                        isLoading={isLoading}
                        fullWidth
                        icon="play_circle"
                        size="lg"
                    >
                        {progress === 0
                            ? t('chest.unlockBtn')
                            : t('chest.watchVideoN', { current: progress + 1, total: 3 })}
                    </Button>

                    {/* Small explanation text below button */}
                    <p className="text-[#1A0B2E]/40 text-xs mt-2">
                        {t('chest.claimCards', { count: rewardCards })}
                    </p>

                    {/* Progress bar */}
                    <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] rounded-full"
                            initial={false}
                            animate={{ width: `${(progress / 3) * 100}%` }}
                            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
